import { EventBus } from '../core/smpl-components'
import { Events } from '../core/events'

import FilterLogic from './filter-logic'
import FilterInterface from './filter-interface'
import FilterDomHandler from './filter-dom-handler'
import { gsap } from 'gsap'

/**
 * FilterCounter
 * --------------------------------------------------------------
 * Handles faceted filtering with:
 *   - OR logic within groups (e.g. phase: design OR research)
 *   - AND logic between groups (e.g. phase + agenda + challenge)
 *
 * How it works:
 * 1. Precompute baseCounts and filterSets:
 *    - baseCounts[group-filter] = total items with that filter
 *    - filterSets[group-filter] = Set of item IDs for fast intersection
 *
 * 2. Compute current result set:
 *    - OR inside each group → union of active filters in that group
 *    - AND across groups → intersection of all unions
 *
 * 3. Determine filter availability:
 *    - Active filters are always enabled (must be toggleable)
 *    - If only one group is active → full OR mode (all filters in that group stay enabled)
 *    - If multiple groups are active (AND mode):
 *         Filters are enabled only if adding them would still produce results
 *
 * This ensures:
 *   - Clean OR behaviour within a group
 *   - Correct AND behaviour between groups
 *   - No dead-ends (active filters never get disabled)
 *   - A predictable, UX-friendly filter experience
 */
class FilterCounter {
	constructor(options) {
		this.options = {
			keepFiltersEnabled: false,
			onCountsUpdate: () => {},
			...options,
		}
		const { filterDomHandler, filterInterface, onCountsUpdate } = this.options
		this.filterDomHandler = filterDomHandler
		this.filterInterface = filterInterface
		this.onCountsUpdate = onCountsUpdate
		this.baseCounts = {}
		this.filterSets = new Map()
	}

	calculateBaseCounts() {
		const filters = this.filterInterface?.filters || []
		const items = this.filterDomHandler?.filterItemsCache || []

		// Counting relies on each item having a unique id (data-id on the element).
		// Without it every item resolves to the same nullish id, the result-set
		// intersections collapse, and filters get wrongly disabled once there is
		// more than one filter group. Warn instead of silently misbehaving.
		if (
			items.length > 0 &&
			items.every((item) => (item._id ?? item.id ?? item.slug) == null)
		) {
			console.warn(
				'[ContentFilter] Counting is enabled but grid items have no unique id. ' +
					'Add a unique `data-id` to each filterable item, or pass `enableCounting: false`. ' +
					'Filters may otherwise be incorrectly disabled.'
			)
		}

		// Initialize counts and sets for all filters
		filters.forEach((f) => {
			if (f.filter === '*') return
			const key = `${f.group}-${f.filter}`
			this.baseCounts[key] = 0
			this.filterSets.set(key, new Set())
		})

		// Populate counts and item sets for each filter
		items.forEach((item) => {
			const id = item._id || item.id || item.slug
			filters.forEach((f) => {
				if (f.filter === '*' || !item.filters.includes(f.filter)) return
				const key = `${f.group}-${f.filter}`
				this.baseCounts[key]++
				this.filterSets.get(key).add(id)
			})
		})
	}

	getCurrentResultSet(filterState, precomputedAllItems = null) {
		const allItems = precomputedAllItems || new Set(this.filterDomHandler.filterItemsCache.map((item) => item._id || item.id))

		return filterState.reduce((result, { group, active }) => {
			if (!active?.length) return result

			// OR within group: union of all active filters in this group
			const union = new Set()
			active.forEach((filter) => {
				const set = this.filterSets.get(`${group}-${filter}`)
				set?.forEach((id) => union.add(id))
			})

			// AND across groups: intersection with current result
			return new Set([...result].filter((id) => union.has(id)))
		}, allItems)
	}

	updateCountsOnFilterChange(filterState) {
		// Optimization: compute allItems once
		const allItems = new Set(this.filterDomHandler.filterItemsCache.map((item) => item._id || item.id))

		const current = this.getCurrentResultSet(filterState, allItems)
		const activeFilters = new Set(filterState.flatMap(({ group, active }) => active?.map((filter) => `${group}-${filter}`) || []))
		const activeGroups = new Set(filterState.filter((g) => g.active?.length).map((g) => g.group))

		// Pre-calculate contexts for active groups
		// For a group G, the context is the intersection of all OTHER active groups.
		const groupContexts = {}
		activeGroups.forEach((g) => {
			const subState = filterState.filter((s) => s.group !== g)
			groupContexts[g] = this.getCurrentResultSet(subState, allItems)
		})

		this.filterInterface.filters.forEach((f) => {
			if (f.filter === '*') return

			const key = `${f.group}-${f.filter}`
			const count = this.baseCounts[key] || 0
			const isActive = activeFilters.has(key)

			// Active filters are always available
			if (isActive) {
				this.updateFilterElement(f.el, count, true)
				return
			}

			// Determine which set of items to check against
			let comparisonSet = current

			// If this filter belongs to a group that is currently active,
			// we shouldn't restrict it by its own group's current selection (because of OR logic).
			// We should check if it fits with the OTHER groups.
			if (activeGroups.has(f.group)) {
				comparisonSet = groupContexts[f.group]
			}

			// Check if filter would return any results
			const filterSet = this.filterSets.get(key)
			const hasResults = filterSet?.intersection?.(comparisonSet).size > 0 || [...comparisonSet].some((id) => filterSet?.has(id))

			// NEW: Skip disabling logic if `disableFilterDisabling` is true
			const isAvailable = this.options.keepFiltersEnabled ? true : hasResults

			// if there is only one filter group active, we are in pure OR mode
			// so all filters in that group should remain enabled
			if (filterState.length <= 1) {
				this.updateFilterElement(f.el, count, true)
				return
			}
			this.updateFilterElement(f.el, count, isAvailable)
		})

		this.onCountsUpdate(this.baseCounts)
	}

	updateFilterElement(el, count, isAvailable) {
		el.setAttribute('data-count', count)
		el.classList.toggle('is-disabled', !isAvailable)

		if ('disabled' in el) el.disabled = !isAvailable

		if (isAvailable) {
			el.removeAttribute('aria-disabled')
			el.removeAttribute('tabindex')
		} else {
			el.setAttribute('aria-disabled', 'true')
			el.setAttribute('tabindex', '-1')
		}
	}

	init() {
		this.calculateBaseCounts()

		// Initialize all filters as available
		this.filterInterface.filters.forEach((f) => {
			if (f.filter !== '*') {
				this.updateFilterElement(f.el, this.baseCounts[`${f.group}-${f.filter}`] || 0, true)
			}
		})

		this.onCountsUpdate(this.baseCounts)
	}
}

class FiltersPanel {
	constructor(options) {
		this.element = options.element

		this.options = { displayStyle: 'block', expandable: false, ...options }

		this.elFiltersInner = this.element.querySelector('.js-filter-interface-inner')
		this.elFiltersInnerWrapper = this.element.querySelector('.filter-interface__filters__wrapper')

		this.elsFiltersToggle = this.element.querySelectorAll('.js-filters-toggle')

		this.isExpanded = this.element.classList.contains('is-expanded') ? true : false
		this.bndOnToggleClick = this.onToggleClick.bind(this)

		if (this.options.expandable) {
			this.elsFiltersToggle.forEach((el) => {
				el.addEventListener('click', this.bndOnToggleClick)
			})
		}
	}
	onToggleClick(ev) {
		ev.preventDefault()
		ev.stopPropagation()
		this.toggle()
	}
	toggle() {
		this.isExpanded = !this.isExpanded
		this.element.classList.toggle('is-expanded', this.isExpanded)

		const panelHeight = this.elFiltersInnerWrapper.offsetHeight
		gsap.to(this.elFiltersInner, {
			height: this.isExpanded ? panelHeight : 0,
			duration: 0.1,
			onComplete: () => {
				if (this.isExpanded) {
					this.elFiltersInner.style.height = ''
				}
			},
		})
	}
	destroy() {
		this.elsFiltersToggle.forEach((el) => {
			el.removeEventListener('click', this.bndOnToggleClick)
		})
		// this.elFiltersToggle.removeEventListener('click', this.bndOnToggleClick)
	}
}

class ContentFilter {
	constructor(options) {
		let defaultOptions = {
			cbOnFilterChange: () => {},
			// Set to false to skip the FilterCounter entirely (no result counts,
			// no auto-disabling of filters that would yield zero results).
			enableCounting: true,
		}

		this.options = Object.assign(defaultOptions, options)

		this.elFilterContainer = this.options.elFilterContainer
		this.elContentContainer = this.options.elContentContainer
		this.elFooter = this.options.elFooter

		var domHandlerOptions = {
			el: this.elContentContainer,
			displayStyle: this.options.displayStyle,
			manualAnimation: false,
			cbOnDomChange: this.onDomChange.bind(this),
			...this.options.domHandlerOptions,
		}
		this.filterDomHandler = new FilterDomHandler(domHandlerOptions)

		// this.gridFilter = new GridFilter({ el: this.elGrid })
		if (this.elFilterContainer) {
			this.filterInterface = new FilterInterface({
				el: this.elFilterContainer,
				filterLogicOptions: this.options.filterLogicOptions || {},
				onClear: this.onClickClear.bind(this),
				onFilterChange: (data) => {
					this.filterDomHandler.applyFilterState(data)

					// Update counts after filter change
					if (this.filterCounter) {
						this.filterCounter.updateCountsOnFilterChange(data)
					}

					if (this.options.cbOnFilterChange) {
						this.options.cbOnFilterChange(data)
					}
				},
			})

			// Create the counter after both filterInterface and filterDomHandler exist
			if (this.options.enableCounting) {
				this.filterCounter = new FilterCounter({
					filterDomHandler: this.filterDomHandler,
					filterInterface: this.filterInterface,
					onCountsUpdate: (counts) => {},
				})

				this.filterCounter.init()
			}
		}

		//
		// deal with expanding stuff
		// if (this.elFilterContainer) {
		// 	this.filtersPanelInstance = new FiltersPanel({
		// 		element: this.elFilterContainer,
		// 	})
		// }

		this.bndOnLoadMore = this.onLoadMore.bind(this)

		if (this.options.elLoadMore) {
			this.options.elLoadMore.addEventListener('click', this.bndOnLoadMore)
		}
	}
	onDomChange(data) {
		EventBus.emit(Events.PAGE_RESIZE)

		if (!data || !this.elFooter) return
		this.elFooter.style.display = data.nrOfVisibleItems >= data.nrOfMatchedItems ? 'none' : 'flex'
	}
	onClickClear() {
		EventBus.emit(Events.FILTERS_CLEAR)
	}
	onLoadMore() {
		if (this.filterDomHandler) {
			this.filterDomHandler.loadMore()
		}
	}
	destroy() {
		this.filterDomHandler.destroy()

		if (this.filtersPanelInstance) {
			this.filtersPanelInstance.destroy()
		}
		if (this.filterInterface) {
			this.filterInterface.destroy()
		}
		// this.gridFilter.destroy()
	}
}

export { FiltersPanel, ContentFilter }
